import React, { useState } from 'react';
import { ClipboardList, Minus, Plus, Search, Filter, PlayCircle, Clock, CheckCircle, AlertCircle, MapPin, User, Users } from 'lucide-react';
interface Job {
  id: number;
  timeStart: string;
  timeEnd: string;
  address: string;
  city: string;
  host: string;
  type: 'Pulizia Standard' | 'Pulizia Profonda' | 'Check-out';
  cleaner: {
    name: string;
    avatarColor: string;
    initials: string;
  };
  status: 'scheduled' | 'in-progress' | 'completed' | 'issue';
  earnings: string;
  lat: number;
  lng: number;
}
interface TeamMember {
  id: string;
  name: string;
  initials: string;
  color: string;
  textColor: string;
  currentLat: number;
  currentLng: number;
}
interface JobsMapPanelProps {
  jobs: Job[];
  activeJobId: number | null;
  onSelect: (id: number) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  teamMembers: TeamMember[];
  selectedMemberId: string | null;
  onMemberSelect: (id: string | null) => void;
}
export function JobsMapPanel({
  jobs,
  activeJobId,
  onSelect,
  isCollapsed,
  onToggleCollapse,
  teamMembers,
  selectedMemberId,
  onMemberSelect
}: JobsMapPanelProps) {
  const [statusFilter, setStatusFilter] = useState<'all' | 'in-progress' | 'scheduled' | 'completed' | 'issue'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.address.toLowerCase().includes(searchQuery.toLowerCase()) || job.host.toLowerCase().includes(searchQuery.toLowerCase()) || job.cleaner.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesMember = selectedMemberId ? (job as any).cleanerId === selectedMemberId : true;
    return matchesSearch && matchesMember;
  });
  // Collapsed state
  if (isCollapsed) {
    return <div className="absolute top-8 left-8 lg:top-12 lg:left-12 z-[500]" data-id="element-756">
        <button onClick={onToggleCollapse} className="flex items-center gap-2.5 bg-white rounded-xl shadow-lg border border-gray-200 px-4 py-3 hover:shadow-xl transition-all group" data-id="element-757">
          <div className="w-8 h-8 rounded-lg bg-teal-deep/10 flex items-center justify-center" data-id="element-758">
            <ClipboardList size={16} className="text-teal-deep" data-id="element-759" />
          </div>
          <div className="text-left" data-id="element-760">
            <p className="text-sm font-semibold text-navy" data-id="element-761">I Miei Lavori</p>
            <p className="text-[11px] text-gray-500" data-id="element-762">
              {jobs.length} interventi
            </p>
          </div>
          <div className="ml-2 w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-teal-deep/10 transition-colors" data-id="element-763">
            <Plus size={14} className="text-gray-500 group-hover:text-teal-deep" data-id="element-764" />
          </div>
        </button>
      </div>;
  }
  // Expanded state
  return <div className="absolute top-8 left-8 lg:top-12 lg:left-12 z-[500] w-[360px] max-h-[calc(100%-64px)] flex flex-col bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden" data-id="element-765">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white" data-id="element-766">
        <div className="flex items-center justify-between mb-3" data-id="element-767">
          <div className="flex items-center gap-2.5" data-id="element-768">
            <h1 className="text-lg font-bold font-montserrat text-navy" data-id="element-769">
              I Miei Lavori
            </h1>
            <span className="bg-teal-deep text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-770">
              {filteredJobs.length}
            </span>
          </div>
          <button onClick={onToggleCollapse} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors" title="Riduci pannello" data-id="element-771">
            <Minus size={14} className="text-gray-600" data-id="element-772" />
          </button>
        </div>

        {/* Team Member Selector */}
        <div className="mb-4" data-id="element-773">
          <div className="flex items-center justify-between mb-2" data-id="element-774">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-775">
              Il Mio Team
            </span>
            {selectedMemberId && <button onClick={() => onMemberSelect(null)} className="text-[10px] text-teal-deep font-medium hover:underline" data-id="element-776">
                Mostra tutti
              </button>}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar" data-id="element-777">
            {teamMembers.map(member => <button key={member.id} onClick={() => onMemberSelect(selectedMemberId === member.id ? null : member.id)} className={`flex flex-col items-center gap-1 min-w-[56px] transition-all ${selectedMemberId === member.id ? 'opacity-100 scale-105' : selectedMemberId ? 'opacity-50' : 'opacity-100 hover:opacity-80'}`} data-id="element-778">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${selectedMemberId === member.id ? 'border-teal-deep shadow-md' : 'border-white shadow-sm'}`} style={{
              backgroundColor: '#1E365C'
            }} data-id="element-779">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-780">
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" data-id="element-781"></path>
                    <circle cx="12" cy="7" r="4" data-id="element-782"></circle>
                  </svg>
                </div>
                <span className={`text-[10px] font-semibold truncate w-full text-center px-0.5 py-px rounded-full border ${selectedMemberId === member.id ? 'text-teal-deep border-teal-deep bg-white' : 'text-navy border-navy bg-white'}`} style={{
              fontSize: '9px',
              lineHeight: '14px'
            }} data-id="element-783">
                  {member.name.split(' ')[0]}
                </span>
              </button>)}
          </div>
        </div>

        {/* Search */}
        <div className="relative" data-id="element-784">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-785" />
          <input type="text" placeholder="Cerca lavoro..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" data-id="element-786" />
        </div>

        {/* Status Filters */}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50/50" data-id="element-787">
        {filteredJobs.length > 0 ? filteredJobs.map(job => <div key={job.id} onClick={() => onSelect(job.id)} className={`bg-white border rounded-xl p-3 cursor-pointer transition-all hover:shadow-md ${activeJobId === job.id ? 'border-teal-deep ring-1 ring-teal-deep shadow-md' : 'border-gray-200'}`} data-id="element-788">
              <div className="flex justify-between items-start mb-2" data-id="element-789">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border text-gray-600 bg-gray-50 border-gray-200" data-id="element-790">
                  {job.type}
                </span>
                <span className="text-xs font-bold text-navy" data-id="element-791">
                  {job.earnings}
                </span>
              </div>

              <div className="flex items-start gap-2 mb-2" data-id="element-792">
                <MapPin size={14} className="text-gray-400 mt-0.5 flex-shrink-0" data-id="element-793" />
                <div data-id="element-794">
                  <p className="text-xs font-bold text-navy leading-tight" data-id="element-795">
                    {job.address}
                  </p>
                  <p className="text-[10px] text-gray-500" data-id="element-796">{job.city}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-[11px] text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-100 mb-3 w-fit" data-id="element-797">
                <Clock size={12} className="text-gray-400" data-id="element-798" />
                <span className="font-medium" data-id="element-799">
                  {job.timeStart} - {job.timeEnd}
                </span>
              </div>

              <div className="flex justify-between items-center pt-2 border-t border-gray-50" data-id="element-800">
                <div className="flex items-center gap-1.5" data-id="element-801">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{
              backgroundColor: '#1E365C'
            }} data-id="element-802">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-803">
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" data-id="element-804"></path>
                      <circle cx="12" cy="7" r="4" data-id="element-805"></circle>
                    </svg>
                  </div>
                  <span className="text-[10px] text-gray-500 truncate max-w-[80px]" data-id="element-806">
                    {job.cleaner.name}
                  </span>
                </div>

                {job.status === 'in-progress' && <span className="flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-100" data-id="element-807">
                    <PlayCircle size={10} data-id="element-808" /> In Corso
                  </span>}
                {job.status === 'completed' && <span className="flex items-center gap-1 text-[10px] font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full border border-green-100" data-id="element-809">
                    <CheckCircle size={10} data-id="element-810" /> Completato
                  </span>}
                {job.status === 'issue' && <span className="flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-100" data-id="element-811">
                    <AlertCircle size={10} data-id="element-812" /> Problema
                  </span>}
                {job.status === 'scheduled' && <span className="flex items-center gap-1 text-[10px] font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-full border border-gray-200" data-id="element-813">
                    <Clock size={10} data-id="element-814" /> Programmato
                  </span>}
              </div>
            </div>) : <div className="text-center py-8 px-4" data-id="element-815">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-816">
              <Filter className="text-gray-400 w-5 h-5" data-id="element-817" />
            </div>
            <p className="text-xs text-gray-500" data-id="element-818">Nessun lavoro trovato</p>
          </div>}
      </div>
    </div>;
}